import React, { useCallback, useEffect, useRef } from 'react'

/** CTS-9778 / CC-UX-014 — speech-focused visualizer tuning */
const BAR_COUNT = 22
const SMOOTHING_TIME_CONSTANT = 0.65
const COACH_BAR_COLOR = '#1c6f29'
const CANDIDATE_BAR_COLOR = '#08467c'
const IDLE_BAR_COLOR = '#9ca3af'
const IDLE_AMPLITUDE = 0.08
const MIN_BAR_HEIGHT_RATIO = 0.08
const FFT_SIZE = 512
/** Gain applied after noise gate (center-weighted) */
const SPEECH_GAIN = 3.4
/** Absolute floor — ignores soft ambient / mic hiss */
const SPEECH_FLOOR = 0.055
/** How far above tracked noise floor a bin must rise to count as speech */
const NOISE_MARGIN = 0.045
const LEVEL_SMOOTH = 0.5
const NOISE_DECAY = 0.995
const NOISE_ATTACK = 0.08

/**
 * Modern audio visualizer: 22 stadium bars growing from a center line.
 * Built separately from AudioVisualizer.js so the legacy LED bars stay intact.
 *
 * @param {Object} props
 * @param {MediaStream|null} props.audioStream
 * @param {boolean} props.isMuted
 * @param {string} props.className
 * @param {number} props.width
 * @param {number} props.height
 * @param {boolean} props.isCoachStream - Coach remote stream → green; local/candidate → blue
 * @param {boolean} props.showWhenMuted
 */
const AudioVisualizerModern = ({
  audioStream,
  isMuted = false,
  className = '',
  width = 145,
  height = 150,
  isCoachStream = false,
  showWhenMuted = false,
}) => {
  const canvasRef = useRef(null)
  const audioContextRef = useRef(null)
  const analyserRef = useRef(null)
  const sourceNodeRef = useRef(null)
  const animationRef = useRef(null)
  const levelsRef = useRef(new Float32Array(BAR_COUNT).fill(IDLE_AMPLITUDE))
  const noiseFloorRef = useRef(new Float32Array(BAR_COUNT).fill(0.04))
  const dataArrayRef = useRef(null)

  const activeBarColor = isCoachStream ? COACH_BAR_COLOR : CANDIDATE_BAR_COLOR

  const drawRoundedBar = useCallback((ctx, x, centerY, barWidth, barHeight) => {
    const top = centerY - barHeight / 2
    const radius = Math.min(barWidth / 2, barHeight / 2)

    ctx.beginPath()
    if (typeof ctx.roundRect === 'function') {
      ctx.roundRect(x, top, barWidth, barHeight, radius)
    } else {
      const r = radius
      ctx.moveTo(x + r, top)
      ctx.lineTo(x + barWidth - r, top)
      ctx.quadraticCurveTo(x + barWidth, top, x + barWidth, top + r)
      ctx.lineTo(x + barWidth, top + barHeight - r)
      ctx.quadraticCurveTo(
        x + barWidth,
        top + barHeight,
        x + barWidth - r,
        top + barHeight,
      )
      ctx.lineTo(x + r, top + barHeight)
      ctx.quadraticCurveTo(x, top + barHeight, x, top + barHeight - r)
      ctx.lineTo(x, top + r)
      ctx.quadraticCurveTo(x, top, x + r, top)
      ctx.closePath()
    }
    ctx.fill()
  }, [])

  const stopAnimation = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current)
      animationRef.current = null
    }
  }, [])

  const disconnectSource = useCallback(() => {
    if (sourceNodeRef.current) {
      sourceNodeRef.current.disconnect()
      sourceNodeRef.current = null
    }
    analyserRef.current = null
    dataArrayRef.current = null
  }, [])

  const startVisualization = useCallback(() => {
    if (!canvasRef.current) {
      return
    }

    stopAnimation()

    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')

    const draw = () => {
      animationRef.current = requestAnimationFrame(draw)

      const canvasWidth = canvas.width
      const canvasHeight = canvas.height
      const centerY = canvasHeight / 2
      const gap = Math.max(2, canvasWidth / (BAR_COUNT * 4))
      const totalGap = gap * (BAR_COUNT + 1)
      const barWidth = Math.max(2, (canvasWidth - totalGap) / BAR_COUNT)
      const maxBarHeight = canvasHeight * 0.85
      const minBarHeight = Math.max(
        barWidth,
        canvasHeight * MIN_BAR_HEIGHT_RATIO,
      )

      const showLive = Boolean(
        analyserRef.current &&
          dataArrayRef.current &&
          (!isMuted || showWhenMuted),
      )

      if (showLive) {
        analyserRef.current.getByteFrequencyData(dataArrayRef.current)
      }

      const dataArray = dataArrayRef.current
      // Skip low rumble / HVAC bins that light up the far-left bars; keep voice mids
      const binCount = dataArray ? dataArray.length : 0
      const speechStart = Math.floor(binCount * 0.1)
      const speechEnd = Math.floor(binCount * 0.42)
      const usableBins = Math.max(1, speechEnd - speechStart)

      for (let i = 0; i < BAR_COUNT; i++) {
        let raw = 0
        if (showLive && usableBins > 0) {
          const start = speechStart + Math.floor((i / BAR_COUNT) * usableBins)
          const end =
            speechStart + Math.floor(((i + 1) / BAR_COUNT) * usableBins)
          let sum = 0
          let count = 0
          for (let j = start; j < Math.max(start + 1, end); j++) {
            sum += dataArray[j] || 0
            count++
          }
          raw = sum / count / 255
        }

        // Adaptive noise floor — rise quickly, fall slowly
        const noise = noiseFloorRef.current[i]
        if (raw < noise) {
          noiseFloorRef.current[i] =
            noise * NOISE_DECAY + raw * (1 - NOISE_DECAY)
        } else {
          noiseFloorRef.current[i] =
            noise * (1 - NOISE_ATTACK) + raw * NOISE_ATTACK * 0.35
        }

        const gated =
          raw > SPEECH_FLOOR && raw > noiseFloorRef.current[i] + NOISE_MARGIN
            ? Math.max(0, raw - noiseFloorRef.current[i])
            : 0

        // Strong center emphasis; damp left/right so ambient edge bins stay quiet
        const t = i / (BAR_COUNT - 1)
        const centerWeight = Math.pow(Math.sin(Math.PI * t), 1.35)
        const shaped = gated * (0.25 + 1.55 * centerWeight)

        const target =
          showLive && shaped > 0
            ? Math.min(1, Math.pow(shaped, 0.75) * SPEECH_GAIN)
            : IDLE_AMPLITUDE
        levelsRef.current[i] =
          levelsRef.current[i] * LEVEL_SMOOTH + target * (1 - LEVEL_SMOOTH)
      }

      ctx.clearRect(0, 0, canvasWidth, canvasHeight)

      for (let i = 0; i < BAR_COUNT; i++) {
        const level = levelsRef.current[i]
        const barHeight = Math.max(minBarHeight, level * maxBarHeight)
        const x = gap + i * (barWidth + gap)
        const isActive = showLive && level > IDLE_AMPLITUDE + 0.02
        ctx.fillStyle = isActive ? activeBarColor : IDLE_BAR_COLOR
        drawRoundedBar(ctx, x, centerY, barWidth, barHeight)
      }
    }

    draw()
  }, [activeBarColor, drawRoundedBar, isMuted, showWhenMuted, stopAnimation])

  const initializeAudio = useCallback(() => {
    if (!audioStream) {
      return
    }

    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext ||
          window.webkitAudioContext)()
      }

      const analyser = audioContextRef.current.createAnalyser()
      analyser.fftSize = FFT_SIZE
      analyser.smoothingTimeConstant = SMOOTHING_TIME_CONSTANT
      analyser.minDecibels = -95
      analyser.maxDecibels = -28

      const sourceNode =
        audioContextRef.current.createMediaStreamSource(audioStream)
      sourceNode.connect(analyser)

      analyserRef.current = analyser
      sourceNodeRef.current = sourceNode
      dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount)

      startVisualization()
    } catch (error) {
      console.warn('Could not initialize modern audio visualizer:', error)
      startVisualization()
    }
  }, [audioStream, startVisualization])

  useEffect(() => {
    disconnectSource()
    stopAnimation()

    if (audioStream) {
      initializeAudio()
    } else {
      startVisualization()
    }

    return () => {
      disconnectSource()
      stopAnimation()
    }
  }, [
    audioStream,
    disconnectSource,
    initializeAudio,
    startVisualization,
    stopAnimation,
  ])

  useEffect(() => {
    return () => {
      disconnectSource()
      stopAnimation()
      if (audioContextRef.current) {
        audioContextRef.current.close()
        audioContextRef.current = null
      }
    }
  }, [disconnectSource, stopAnimation])

  return (
    <canvas
      ref={canvasRef}
      className={`audio-visualizer-modern ${className}`}
      width={width}
      height={height}
      style={{ width: `${width}px`, height: `${height}px`, maxWidth: '100%' }}
      aria-label={`${isCoachStream ? 'Coach' : 'Local'} audio visualization`}
    />
  )
}

export default AudioVisualizerModern
